import { ORDER_STATUS, OFFLINE_ORDER_STATUS } from '../constants/orderConstants';
import { normalizePaymentStatus } from './paymentStatus';

const ADD_STATUSES = [
  ORDER_STATUS.PENDING_CONFIRMATION,
  ORDER_STATUS.PENDING_GENERATION,
  ORDER_STATUS.PENDING_PRINTING,
  ORDER_STATUS.PENDING_DELIVERY
];
const REPLACE_STATUSES = [ORDER_STATUS.PENDING_CONFIRMATION, ORDER_STATUS.PENDING_GENERATION];
const REMOVE_STATUSES = [ORDER_STATUS.PENDING_CONFIRMATION, ORDER_STATUS.PENDING_GENERATION];
const ADDON_STATUSES = ADD_STATUSES;
// A story swap stays possible after the staged-edit window closes; the backend
// route also accepts it while a generation asks for help (checked by the caller).
const SWAP_STATUSES = [
  ORDER_STATUS.PENDING_CONFIRMATION,
  ORDER_STATUS.PENDING_GENERATION,
  OFFLINE_ORDER_STATUS.PLACED
];

export const MIN_OVERRIDE_REASON_LENGTH = 10;

// Every method the backend accepts for an additional payment (all except cod).
export const ADDITIONAL_PAYMENT_METHODS = [
  { value: 'online_payment', label: 'Online Payment' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'instapay', label: 'InstaPay' },
  { value: 'mobile_wallet', label: 'Mobile Wallet' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' }
];

export const getPaymentMethodLabel = method => {
  const known = ADDITIONAL_PAYMENT_METHODS.find(m => m.value === method);
  if (known) return known.label;
  const text = String(method || '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Unknown';
};

const toAmount = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const roundCents = value => Math.round((value + Number.EPSILON) * 100) / 100;

// A missing method counts as COD, matching the backend edit policy.
const isCod = order => !order?.payment?.method || order.payment.method === 'cod';

export function isPaidOnline(order) {
  if (isCod(order)) return false;
  return ['paid', 'collected'].includes(normalizePaymentStatus(order.payment.status));
}

// Mirrors backend orderEditPolicy.getPaymentBalance.
export function getPaymentBalance(order) {
  const payment = order?.payment && typeof order.payment === 'object' ? order.payment : {};
  const total = toAmount(order?.total_price);
  const hasPaid = payment.paid_amount !== undefined
    && payment.paid_amount !== null
    && payment.paid_amount !== ''
    && Number.isFinite(Number(payment.paid_amount));

  let paid = 0;
  if (hasPaid) paid = Number(payment.paid_amount);
  else if (isPaidOnline(order)) paid = toAmount(payment.amount);

  const due = isCod(order) ? total : Math.max(0, total - paid);
  return {
    total: roundCents(total),
    paid_amount: roundCents(paid),
    amount_due: roundCents(due)
  };
}

export function getEditAvailability(order) {
  const status = String(order?.status || '').toLowerCase();
  const label = status ? status.replace(/_/g, ' ') : 'unknown';

  if (order?.source === 'offline') {
    const reason = 'Offline orders cannot be edited here';
    return { canAdd: false, canReplace: false, addReason: reason, replaceReason: reason };
  }
  if (order?.needs_edit_repair === true) {
    const reason = 'Order is flagged for repair';
    return { canAdd: false, canReplace: false, addReason: reason, replaceReason: reason };
  }

  const canAdd = ADD_STATUSES.includes(status);
  const canReplace = REPLACE_STATUSES.includes(status);
  const result = { canAdd, canReplace };
  if (!canAdd) result.addReason = `Stories cannot be added to an order that is ${label}`;
  if (!canReplace) result.replaceReason = `Stories can only be replaced while an order is pending confirmation or pending generation (currently ${label})`;
  return result;
}

// Mirrors the backend status windows for the full edit mode.
export function getEditCapabilities(order, { generationIssue } = {}) {
  const status = String(order?.status || '').toLowerCase();
  const label = status ? status.replace(/_/g, ' ') : 'unknown';
  const none = reason => ({
    canEdit: false,
    reason,
    canAdd: false,
    canReplace: false,
    canRemove: false,
    canSwitchPrinting: false,
    canToggleAddons: false,
    canDiscount: false,
    canSwapStory: false,
    reasons: {}
  });

  if (order?.source === 'offline') return none('Offline orders cannot be edited here');
  if (order?.needs_edit_repair === true) return none('Order is flagged for repair');

  const canAdd = ADD_STATUSES.includes(status);
  const canReplace = REPLACE_STATUSES.includes(status);
  const canRemove = REMOVE_STATUSES.includes(status);
  const canSwitchPrinting = REMOVE_STATUSES.includes(status);
  const canToggleAddons = ADDON_STATUSES.includes(status);
  const canDiscount = ADD_STATUSES.includes(status);
  const canSwapStory = SWAP_STATUSES.includes(status) || generationIssue?.type === 'help';

  const reasons = {};
  if (!canAdd) reasons.add = `Stories cannot be added to an order that is ${label}`;
  if (!canReplace) reasons.replace = `Stories can only be replaced while an order is pending confirmation or pending generation (currently ${label})`;
  if (!canRemove) reasons.remove = `Stories can only be removed while an order is pending confirmation or pending generation (currently ${label})`;
  if (!canSwitchPrinting) reasons.switchPrinting = `Printing can only be changed while an order is pending confirmation or pending generation (currently ${label})`;
  if (!canToggleAddons) reasons.addons = `Add-ons cannot be changed on an order that is ${label}`;
  if (!canDiscount) reasons.discount = `Discounts cannot be changed on an order that is ${label}`;
  if (!canSwapStory) reasons.swap = `Stories can only be swapped while the order is pending confirmation, pending generation, placed, or the generation asked for help (currently ${label})`;

  const canEdit = canAdd || canReplace || canRemove || canSwitchPrinting || canToggleAddons || canDiscount || canSwapStory;
  const result = { canEdit, canAdd, canReplace, canRemove, canSwitchPrinting, canToggleAddons, canDiscount, canSwapStory, reasons };
  if (!canEdit) result.reason = `Orders that are ${label} cannot be edited`;
  return result;
}

// Display help only; the server enforces the real limit.
export function getMaxDiscount(order, lineSum) {
  const sum = toAmount(lineSum);
  if (isCod(order)) return roundCents(sum);
  return roundCents(Math.max(0, sum - getPaymentBalance(order).paid_amount));
}

// Field-level override errors: { price?: string, reason?: string }.
export function getOverrideErrors(priceInput, reasonInput) {
  const priceText = String(priceInput ?? '').trim();
  const reason = String(reasonInput ?? '').trim();
  const errors = {};
  if (!priceText && !reason) return errors;
  if (!priceText) {
    errors.price = 'Enter an override price or clear the reason';
    return errors;
  }
  const price = Number(priceText);
  if (!Number.isFinite(price)) {
    errors.price = 'Enter a valid price';
  } else if (price < 0) {
    errors.price = 'Price must be at least 0';
  } else if (!/^\d+(\.\d+)?$/.test(priceText)) {
    errors.price = 'Enter a valid price';
  } else if (/\.\d{3,}$/.test(priceText)) {
    errors.price = 'At most 2 decimal places';
  }
  if (reason.length < MIN_OVERRIDE_REASON_LENGTH) {
    errors.reason = `Reason must be at least ${MIN_OVERRIDE_REASON_LENGTH} characters`;
  }
  return errors;
}

// Returns the first error string, or null when the override is valid or absent.
export function validatePriceOverride(priceInput, reasonInput) {
  const errors = getOverrideErrors(priceInput, reasonInput);
  return errors.price || errors.reason || null;
}
